/**
 * ReportIssueModal — Reusable card / data issue reporting modal
 *
 * Can be dropped into any page that displays card data. Submits a
 * support ticket via the existing support service.
 */

import { hasCardNumber } from '../../utils/cardNumber'
import React, { useState } from 'react'
import { createTicket } from '../../services/support.service'
import { Flag, Check, X } from 'lucide-react'
import { isSupportedCurrency, type Currency } from '../../contexts/CurrencyContext'
import styles from './ReportIssueModal.module.css'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ReportIssueContext {
  /** Human-readable card name */
  cardName: string
  cardId?: string
  setName?: string
  setCode?: string
  setId?: string
  setNumber?: string
  gameName?: string
  rarity?: string
  /** Currently selected printing / condition labels */
  printing?: string
  condition?: string
  /** Price in cents, in {@link priceCurrency} */
  priceCents?: number | null
  /**
   * Currency `priceCents` is in. Absent means USD (a catalogue / market price); an inventory
   * row's listed price is already in the store's currency and must not be converted again.
   */
  priceCurrency?: string | null
  /** Variant id (if any) */
  variantId?: string | null
  /** Which page the report originated from */
  source?: string
  /** Any extra metadata to attach */
  extra?: Record<string, any>
}

export interface ReportIssueModalProps {
  isOpen: boolean
  onClose: () => void
  context: ReportIssueContext
  /** Currency formatter — e.g. `formatPrice` from CurrencyContext */
  formatPrice?: (cents: number, fromCurrency?: Currency) => string
}

/* ------------------------------------------------------------------ */
/*  Issue catalogue                                                    */
/* ------------------------------------------------------------------ */

const REPORT_ISSUES = [
  { value: 'wrong_price', label: 'Price is incorrect / too low / too high' },
  { value: 'wrong_image', label: 'Image is wrong or missing' },
  { value: 'wrong_name', label: 'Card name is incorrect' },
  { value: 'wrong_set', label: 'Card is in the wrong set' },
  { value: 'wrong_rarity', label: 'Rarity is incorrect' },
  { value: 'missing_variants', label: 'Variants are missing or incomplete' },
  { value: 'other', label: 'Other issue' },
] as const

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
  isOpen,
  onClose,
  context,
  formatPrice,
}) => {
  const [issue, setIssue] = useState('')
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleClose = () => {
    if (submitting) return
    setIssue('')
    setDetails('')
    setSuccess(false)
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    if (!issue) return

    const issueLabel = REPORT_ISSUES.find(i => i.value === issue)?.label || issue
    const priceCurrency = (context.priceCurrency ?? 'USD').toUpperCase()
    const priceStr =
      context.priceCents != null && context.priceCents > 0 && formatPrice && isSupportedCurrency(priceCurrency)
        ? formatPrice(context.priceCents, priceCurrency)
        : context.priceCents != null && context.priceCents > 0
          ? `${priceCurrency} ${(context.priceCents / 100).toFixed(2)}`
          : 'N/A'

    const description = [
      `Issue: ${issueLabel}`,
      details ? `Details: ${details}` : null,
      '',
      '--- Card Info ---',
      `Card: ${context.cardName}`,
      context.cardId ? `Card ID: ${context.cardId}` : null,
      context.setName ? `Set: ${context.setName}${context.setCode ? ` (${context.setCode})` : ''}` : null,
      hasCardNumber(context.setNumber) ? `Number: #${context.setNumber}` : null,
      context.gameName ? `Game: ${context.gameName}` : null,
      context.rarity ? `Rarity: ${context.rarity}` : null,
      context.printing || context.condition
        ? `Variant: ${context.printing || 'Any'} / ${context.condition || 'Any'} — ${priceStr}`
        : null,
      context.source ? `Source Page: ${context.source}` : null,
    ]
      .filter(Boolean)
      .join('\n')

    setSubmitting(true)
    setError(null)
    try {
      await createTicket({
        category: 'data_issue',
        priority: issue === 'wrong_price' ? 'high' : 'normal',
        title: `Card Issue: ${context.cardName} — ${issueLabel}`,
        description,
        context: {
          ...context.extra,
          cardId: context.cardId,
          cardName: context.cardName,
          setId: context.setId,
          setName: context.setName,
          setCode: context.setCode,
          issueType: issue,
          variantId: context.variantId || null,
          variantPrinting: context.printing || null,
          variantCondition: context.condition || null,
          variantPrice: context.priceCents ?? null,
          variantPriceCurrency: context.priceCents != null ? priceCurrency : null,
          source: context.source,
        },
      })
      setSuccess(true)
    } catch (err: any) {
      console.error('Failed to submit card report:', err)
      setError(err.response?.data?.message || 'Failed to submit report. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3><Flag size={18} /> Report Card Issue</h3>
          <button type="button" className={styles.closeBtn} onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className={styles.body}>
            <div className={styles.successMessage}>
              <Check size={24} />
              <div>
                <strong>Report submitted — thank you!</strong>
                <p>
                  We'll review the issue with <em>{context.cardName}</em> and work on a fix.
                  You can track your report in Account → Support.
                </p>
              </div>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.primaryBtn} onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.body}>
            <p className={styles.subtext}>
              Report an issue with <strong>{context.cardName}</strong>
              {context.setName ? ` (${context.setName})` : ''}
            </p>

            {error && (
              <div className={styles.errorMsg}>{error}</div>
            )}

            <label className={styles.label}>
              What's wrong?
              <select
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
                className={styles.select}
              >
                <option value="">Select an issue…</option>
                {REPORT_ISSUES.map((i) => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </label>

            {issue && (
              <label className={styles.label}>
                Additional details {issue !== 'other' ? '(optional)' : '(required)'}
                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  className={styles.textarea}
                  rows={3}
                  placeholder={
                    issue === 'wrong_price'
                      ? 'e.g. "This card should be ~$35, not $0.89"'
                      : issue === 'other'
                        ? 'Please describe the issue…'
                        : 'Any extra context that might help us fix this…'
                  }
                />
              </label>
            )}

            <div className={styles.actions}>
              <button type="button" className={styles.cancelBtn} onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleSubmit}
                disabled={submitting || !issue || (issue === 'other' && !details.trim())}
              >
                {submitting ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReportIssueModal
